import { DOMParser, XMLSerializer } from '@xmldom/xmldom';

const xmlDomUtils = {
    getChildNodes(xmlElement, tagName) {
        return Array.from(
            xmlElement.getElementsByTagName(tagName)
        );
    },

    getChildElements(xmlElement) {
        return Array
            .from(xmlElement.childNodes)
            .filter(node => node.nodeType === 1);
    },

    getAttributes(xmlElement) {
        return Array
            .from(xmlElement.attributes)
            .reduce((accumulator, attribute) => {
                accumulator[attribute.nodeName] = attribute.textContent;
                return accumulator;
            }, {});
    },

    hasClass(xmlElement, className) {
        return this.getClassList(xmlElement).includes(className);
    },

    hasClasses(xmlElement, classNames) {
        return classNames.every(className => this.hasClass(xmlElement, className));
    },

    removeClass(xmlElement, className) {
        const classesString = this
            .getClassList(xmlElement)
            .filter(cName => cName !== className)
            .join(' ');
        xmlElement.setAttribute('class', classesString);
    },

    getXmlDocFromString(xmlString) {
        return new DOMParser().parseFromString(xmlString, 'text/xml');
    },

    getElementFromString(xmlString) {
        if (!xmlString) {
            return null;
        }
        const xmlDoc = this.getXmlDocFromString(this.wrapHtmlStringIfNeeded(xmlString));
        return xmlDoc?.firstChild;
    },

    isDomParsableString(xmlString) {
        if (!xmlString || typeof xmlString !== 'string') {
            return false;
        }
        const reg = /<\/|\/>|<br|<img /gi;
        return reg.test(xmlString);
    },

    wrapHtmlStringIfNeeded(htmlString, wrapperTag = 'span') {
        const trimmedHtmlString = htmlString ? htmlString.trim() : '';
        if (!trimmedHtmlString.startsWith('<') && trimmedHtmlString.includes('<')) {
            return `<${wrapperTag}>${htmlString}</${wrapperTag}>`;
        }
        return htmlString;
    },

    getClassList(xmlElement) {
        const attributes = this.getAttributes(xmlElement);
        const classAttribute = attributes.class || '';
        return classAttribute.split(/\s+/).filter(className => className.length > 0);
    },

    serializeNodeWithoutNamespace(node) {
        const serialized = new XMLSerializer().serializeToString(node);

        // Remove namespace declarations without using regex to avoid ReDoS vulnerabilities
        let result = serialized;
        let searchPos = 0;

        while (searchPos < result.length) {
            // Find next occurrence of ' xmlns' or ' xmlns:'
            const xmlnsIndex = result.indexOf(' xmlns', searchPos);
            if (xmlnsIndex === -1) {
                break;
            }

            // Find the '="' part
            const equalsIndex = result.indexOf('="', xmlnsIndex);
            if (equalsIndex !== -1 && equalsIndex - xmlnsIndex <= 50) {
                // Find the closing quote, with a reasonable limit
                let closeQuoteIndex = -1;
                const maxSearch = equalsIndex + 2 + 1000; // Start of value + reasonable URI length
                for (let i = equalsIndex + 2; i < result.length && i < maxSearch; i++) {
                    if (result[i] === '"') {
                        closeQuoteIndex = i;
                        break;
                    }
                }

                if (closeQuoteIndex !== -1) {
                    // Remove the xmlns attribute (from space before xmlns to closing quote)
                    result = result.substring(0, xmlnsIndex) + result.substring(closeQuoteIndex + 1);
                    // Continue searching from where we removed
                    searchPos = xmlnsIndex;
                } else {
                    // No closing quote found, skip
                    searchPos = xmlnsIndex + 6;
                }
            } else {
                // Malformed or too far apart, skip
                searchPos = xmlnsIndex + 6;
            }
        }

        // Unescape &gt; to > in element content using simple string replacement
        // This is safe as we're just doing a literal string replacement
        result = result.split('&gt;').join('>');

        return result;
    },

    stringify(xmlElement) {
        // Handle NodeList by serializing each node separately
        if (xmlElement?.length !== undefined) {
            return Array.from(xmlElement)
                .map(node => this.serializeNodeWithoutNamespace(node))
                .join('');
        }
        return new XMLSerializer().serializeToString(xmlElement);
    },
};

export default xmlDomUtils;
